import { useCallbackRef } from '@chakra-ui/react';
import { useMemo } from 'react';

import { useAnalytics } from '@/context/Analytics';
import { OriginalsVideoEvents } from '@/enums/VideoEvent';
import { useLibLynx } from '@/hooks/useLibLynx';
import { SlugIdParam, useQueryParams } from '@/hooks/useQueryParams';
import { extractPlayerAttributes } from '@/hooks/videojs/videojs-utils';
import { ILibLynxMetadata } from '@/types/LibLynx';

export const useOriginalVideoEvents = (episode) => {
  const analytics = useAnalytics();
  const libLynx = useLibLynx();

  const { id: seriesId } = useQueryParams<{ id: string }>({
    id: SlugIdParam,
  });

  const sendLibLynxAnalyitics = useCallbackRef(() => {
    const libLynxMetadata: ILibLynxMetadata = { dtype: 'multimedia', id: episode?.id, title: episode?.title };

    libLynx.send('trackItemRequest', { im: libLynxMetadata });
  });

  return useMemo(() => {
    const defaultParameters = {
      originals_id: seriesId,
      originals_episode_name: episode?.title,
      originals_epsiode_id: episode?.id,
    };

    return {
      onPlay: (playerEvent) => {
        const player = playerEvent.target.player;

        sendLibLynxAnalyitics();

        analytics.track(OriginalsVideoEvents.Play, { ...defaultParameters, ...extractPlayerAttributes(player) });
      },
      onResume: (playerEvent) => {
        const player = playerEvent.target.player;

        analytics.track(OriginalsVideoEvents.Resume, { ...defaultParameters, ...extractPlayerAttributes(player) });
      },
      onPause: (playerEvent) => {
        const player = playerEvent.target.player;

        analytics.track(OriginalsVideoEvents.Pause, { ...defaultParameters, ...extractPlayerAttributes(player) });
      },
      onEnded: (playerEvent) => {
        const player = playerEvent.target.player;

        analytics.track(OriginalsVideoEvents.Ended, { ...defaultParameters, ...extractPlayerAttributes(player) });
      },
      onDispose: (playerEvent) => {
        const player = playerEvent.target.player;

        analytics.track(OriginalsVideoEvents.Dispose, { ...defaultParameters, ...extractPlayerAttributes(player) });
      },
    };
  }, [analytics, episode, seriesId, sendLibLynxAnalyitics]);
};
